import type { FileRouteTypes } from "@/routeTree.gen";
import type { UserRole } from "@/contexts/AuthContext";

/**
 * A path this app actually has, as the router's own generated union.
 *
 * **Not `string`, and that is not pedantry.** The ternary chain this map
 * replaced fed its literals straight to `<Navigate to=…>`, which is typed
 * against this union — so `/admin/dashbord` was a compile error. Typing the map
 * as `string` silently gave that up: the typo would compile, satisfy every test
 * here (they check the area prefix, not the spelling), and land a super admin
 * on the NotFound page. Borrowing the router's type puts the check back where
 * it was and makes it stronger, since it now guards a named map rather than an
 * expression buried in a component.
 */
type AppPath = FileRouteTypes["to"];

/**
 * Where a role lands when it arrives with nowhere in particular to be.
 *
 * **One map, because two readers have to agree.** `/home` sends a session on
 * to its dashboard, and the root gate decides whether a session sitting on
 * `/no-access` should be sent back into the app. Those two questions are the
 * same question, and answering them separately is what produced an infinite
 * redirect: `/home` fell through to the end-user dashboard for every role it
 * did not name, `/end-user` admits four roles and bounces the rest to `/home`,
 * and `/home` fired again. Nothing bounded it — the chain restarts on a
 * component mount, so the tab simply spun.
 *
 * Every role in the vocabulary has an area that admits it, so the map holds a
 * path for each. A role added to `USER_ROLES` fails the build here until it is
 * given one — and if it has no screens, the answer is to leave it out of the
 * vocabulary, where `normalizeRole` turns it into no role and the no-access
 * page, rather than to land it somewhere its area guard refuses.
 */
const ROLE_LANDING: Record<UserRole, AppPath> = {
  super_admin: "/admin/dashboard",
  bu_admin: "/bu-admin/dashboard",
  group_legal: "/group-legal/dashboard",
  end_user: "/end-user/dashboard",
};

/**
 * The path this role should land on, or null for a session with no role.
 *
 * Exhaustive by construction: `Record<UserRole, …>` means adding a role to
 * `USER_ROLES` without deciding its landing fails the build, which is the
 * whole point of putting it here rather than in a ternary chain.
 */
export function landingPathFor(role: UserRole | null): AppPath | null {
  return role ? ROLE_LANDING[role] : null;
}
